import React, { useMemo, useState } from "react";
import moment, { Moment } from "moment-timezone";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker, renderTimeViewClock } from "@mui/x-date-pickers";
import { ErrorMessage } from "../ErrorMessage/ErrorMessage";
import { Box, Typography } from "@mui/material";
import { CustomFormLabel } from "./CustomFormLabel";
import { styled } from "@mui/material/styles";
import { FIELD_HIGHLIGHT_SX } from "../../shared";

export const MuiDatePicker = styled(DatePicker, {
  shouldForwardProp: (prop) => prop !== "highlight",
})<{ highlight?: boolean }>(({ theme, highlight }) => ({
  /* ---------- ROOT ---------- */
  "& .MuiOutlinedInput-root": {
    borderRadius: "5px",
    fontSize: "0.8rem",
    color: "#454545",
    // Standardise field height across all filter inputs.
    height: 38,
  },

  /* ---------- INPUT ---------- */
  "& .MuiOutlinedInput-input": {
    padding: "0 4px",
    height: "38px",
    boxSizing: "border-box",
    fontSize: "0.85rem",
    fontWeight: 600,
    color: "#454545",
  },

  /* ---------- PLACEHOLDER ---------- */
  "& .MuiOutlinedInput-input::placeholder": {
    color: "#767e89",
    opacity: 1,
  },

  /* ---------- BORDER ---------- */
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor:
      theme.palette.mode === "dark" ? "rgba(255,255,255,0.12)" : "#dee3e9",
  },

  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#c3c9d1",
  },

  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#5a5a5a",
    borderWidth: "1px",
  },

  "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
    borderColor: "#dc2626",
  },

  /* ---------- LABEL ---------- */
  "& .MuiInputLabel-root": {
    fontSize: "0.8rem",
    marginTop: "-0.1rem",
    color: "#757575",
    padding: "0 6px"
  },
  "& .MuiInputLabel-root.Mui-focused": {
    // Keep the default label colour on focus (no primary/blue tint).
    color: theme.palette.mode === "dark" ? "#e6e5e8" : "#5a5a5a",
  },
  "& .MuiInputLabel-root.Mui-error": {
    color: "#dc2626",
  },

  "& .MuiInputLabel-shrink": {
    fontSize: "0.8rem !important",
    fontWeight: "600 !important",
    transform: "translate(14px, -9px) scale(1)",
    color: "#5a5a5a",
    padding: "0 6px",
    // backgroundColor: theme.palette.mode === "dark" ? "#282C34" : "#ffffff",
    // backgroundColor: "inherit",
    // backgroundColor: "var(--parent-bg)",
    borderRadius: "3px",
  },

  /* ---------- DISABLED ---------- */
  "& .MuiOutlinedInput-root.Mui-disabled": {
    backgroundColor:
      theme.palette.mode === "dark" ? "rgba(0,0,0,0.12)" : "#f8f9fb",
  },

  "& .MuiOutlinedInput-input.Mui-disabled": {
    WebkitTextFillColor: "rgba(0,0,0,0.55)",
    fontWeight: 600,
  },

  /* ---------- ICON ---------- */
  "& .MuiSvgIcon-root": {
    color: "#757575",
    fontSize: "1.1rem",
  },

  "& .MuiIconButton-root:hover .MuiSvgIcon-root": {
    color: "#5a5a5a",
  },

  /* ---------- HIGHLIGHT STYLING ---------- */
  ...(highlight ? FIELD_HIGHLIGHT_SX(theme) : {}),
}));

export const CustomDatePicker: React.FC<{
  value: string | null;
  placeholder?: string;
  sx?: any;
  disabled?: boolean;
  views?: ("day" | "month" | "year")[];
  inputFormat?: string;
  fullWidth?: boolean;
  errorMessage?: string;
  minDate?: any;
  maxDate?: any;
  onChange: (
    value: any,
    timeWithSeconds: any,
    momentObject: Moment | null,
  ) => void;
  error?: boolean;
  highlight?: boolean;
}> = (props) => {
  const {
    value,
    sx,
    placeholder,
    views = ["year", "month", "day"],
    inputFormat = "DD-MM-YYYY",
    fullWidth,
    disabled,
    errorMessage,
    minDate,
    maxDate,
    error,
    highlight,
  } = props;

  const [open, setOpen] = useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const isMonthDayOnly =
    views.length === 2 && views.includes("month") && views.includes("day");

  React.useEffect(() => {
    if (!open) return;

    const handleOutsideClick = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      if (!target) return;

      const isInsideInput =
        containerRef.current && containerRef.current.contains(target);
      const isInsidePopper = Boolean(
        (target as Element).closest?.(
          ".MuiPickersPopper-root, .custom-datepicker-popper, .MuiDialog-root",
        ),
      );

      if (!isInsideInput && !isInsidePopper) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick, true);
    document.addEventListener("touchstart", handleOutsideClick, true);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick, true);
      document.removeEventListener("touchstart", handleOutsideClick, true);
    };
  }, [open]);

  return (
    <Box
      ref={containerRef}
      sx={{
        width: fullWidth ? "100%" : "unset",
        display: fullWidth ? "block" : "inline-block",
      }}
    >
      <MuiDatePicker
        highlight={highlight}
        open={disabled ? false : open}
        onOpen={() => !disabled && setOpen(true)}
        onClose={() => setOpen(false)}
        closeOnSelect={true}
        views={views}
        label={placeholder}
        disabled={disabled}
        format={isMonthDayOnly ? "DD MMM" : inputFormat}
        sx={{
          width: fullWidth ? "100%" : "unset",
          ...sx,
        }}
        minDate={minDate}
        maxDate={maxDate}
        value={
          value ? moment(value, isMonthDayOnly ? "DD-MM" : undefined) : null
        }
        onChange={(newValue) => {
          if (!newValue) {
            props.onChange(null, null, null);
            return;
          }

          if (isMonthDayOnly) {
            const dateOnly = moment(newValue).format("DD-MM");
            props.onChange(dateOnly, null, moment(newValue));
          } else {
            const fullDate = moment(newValue).format("YYYY-MM-DD");
            const fullDateWithTime = moment(newValue).format(
              "YYYY-MM-DD, HH:mm:ss a",
            );
            props.onChange(fullDate, fullDateWithTime, moment(newValue));
          }

          setOpen(false); // close after selection
        }}
        slotProps={{
          popper: {
            className: "custom-datepicker-popper",
          },
          textField: {
            fullWidth: true,
            disabled: disabled,
            error: Boolean(errorMessage),
            helperText: errorMessage,
            InputLabelProps: { shrink: true },
            onClick: () => !disabled && setOpen(true),
          },
        }}
      />
      {/* {errorMessage && <ErrorMessage value={errorMessage} />} */}
    </Box>
  );
};

export const CustomShrinkDatePicker: React.FC<{
  value: string;
  label: string;
  sx?: any;
  disabled?: boolean;
  views?: ("day" | "month" | "year")[];
  inputFormat?: string;
  fullWidth?: boolean;
  errorMessage?: string;
  onChange: (
    value: any,
    timeWithSeconds: any,
    momentObject: Moment | null,
  ) => void;
}> = (props) => {
  const {
    value,
    sx,
    views = ["year", "month", "day"],
    inputFormat = "DD-MM-YYYY",
    fullWidth,
    disabled,
    errorMessage,
  } = props;
  return (
    <>
      <DatePicker
        views={views}
        label={props.label}
        disabled={disabled}
        format={inputFormat}
        sx={{
          width: fullWidth ? "100%" : "unset",

          "& .MuiInputLabel-shrink": {
            fontSize: "1.3rem",
            fontWeight: 600, // Increase the font size here
          },

          "& .MuiInputLabel-shrink + .MuiOutlinedInput-root": {
            "& fieldset": {
              legend: {
                maxWidth: "100%", // Ensure it does not overflow
                fontSize: "1.05rem",
              },
            },
          },
          ...sx,
        }}
        value={value ? moment(value) : null}
        onChange={(newValue) => {
          if (newValue) {
            const newDate = moment(newValue).format("YYYY-MM-DD");
            const newDate1 = moment(newValue).format("YYYY-MM-DD, HH:mm:ss a");
            props.onChange(newDate, newDate1, newValue);
          } else {
            props.onChange(null, null, null);
          }
        }}
      />
      {errorMessage && <ErrorMessage value={errorMessage} />}
    </>
  );
};

export const CustomTimePicker: React.FC<{
  value: string | null;
  sx?: any;
  fullWidth?: boolean;
  onChange: (value: string) => void;
  disabled?: boolean;
}> = (props) => {
  const { value, sx, fullWidth, disabled, onChange } = props;

  // Handle null/undefined values and invalid dates
  const momentValue = useMemo(() => {
    if (!value) return null;
    const time = moment(value, "HH:mm:ss");
    return time.isValid() ? time : null;
  }, [value]);

  return (
    <TimePicker
      value={momentValue}
      disabled={disabled}
      viewRenderers={{
        hours: renderTimeViewClock,
        minutes: renderTimeViewClock,
      }}
      format="HH:mm"
      onChange={(newValue) => {
        if (newValue && newValue.isValid()) {
          // Format as HH:mm:ss
          const formatted = newValue.format("HH:mm:ss");
          onChange(formatted);
        } else {
          onChange("");
        }
      }}
      sx={{
        width: fullWidth ? "100%" : "unset",
        "& .MuiOutlinedInput-input": {
          padding: "9px 13px",
          fontSize: "0.8rem",
          color: "rgb(38, 38, 38)",
        },
        ...sx,
      }}
    />
  );
};

export const CustomDateRangeFilter: React.FC<{
  startDate: string | null;
  endDate: string | null;
  onStartDateChange: (date: string | null) => void;
  onEndDateChange: (date: string | null) => void;
  errorMessage?: string;
  minDate?: Date;
  maxDate?: Date;
}> = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  errorMessage,
  minDate,
  maxDate,
}) => {
    const handleStartDateChange = (newDate: string | null) => {
      onStartDateChange(newDate);
      // Reset end date if it's before new start date
      if (newDate && endDate && moment(endDate).isBefore(moment(newDate))) {
        onEndDateChange(null);
      }
    };

    return (
      <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
        <Box flex={1}>
          <CustomFormLabel>From</CustomFormLabel>
          <CustomDatePicker
            value={startDate}
            onChange={(newValue) => handleStartDateChange(newValue)}
            inputFormat="DD-MM-YYYY"
            minDate={moment().subtract(10, "years").toDate()} // Adjust as needed
            maxDate={maxDate}
          />
        </Box>

        <Box flex={1}>
          <CustomFormLabel>To</CustomFormLabel>
          <CustomDatePicker
            value={endDate}
            onChange={(newValue) => onEndDateChange(newValue)}
            inputFormat="DD-MM-YYYY"
            minDate={startDate ? moment(startDate).toDate() : undefined}
            maxDate={maxDate}
          />
          {errorMessage && (
            <Typography
              variant="caption"
              color="error"
              sx={{ mt: 0.5, display: "block" }}
            >
              {errorMessage}
            </Typography>
          )}
        </Box>
      </Box>
    );
  };
